/**
 * 카카오맵 JS SDK 동적 로더 (mimio#30/#33).
 *
 * 앱 전역에서 항상 받는 게 아니라 지도 화면에 들어올 때만 부른다 — 전시 지도 하나 때문에
 * 전 페이지 초기 번들에 외부 SDK 를 얹을 이유가 없다.
 *
 * 실패는 조용히 넘기지 않는다. 특히 `domain mismatched`(401) 는 코드 문제가 아니라
 * 카카오 개발자 콘솔에 현재 오리진이 등록되지 않아서 나는 것이라, 화면에서 그 사실과
 * 등록해야 할 오리진을 그대로 보여줘야 한다. 조용히 빈 지도가 되면 원인 추적이 불가능하다.
 */

const SDK_ID = 'kakao-maps-sdk';

/** 중복 로드 방지 — 라우트를 오갈 때마다 script 태그가 쌓이면 안 된다. */
let loadPromise = null;

export const KAKAO_MAP_ERROR = {
  NO_KEY: 'NO_KEY',
  LOAD_FAILED: 'LOAD_FAILED',
};

export class KakaoMapLoadError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'KakaoMapLoadError';
    this.code = code;
    this.origin = typeof window !== 'undefined' ? window.location.origin : '';
  }
}

/**
 * SDK 를 로드하고 `window.kakao.maps` 가 준비된 상태로 resolve 한다.
 *
 * @param {string[]} libraries 추가 라이브러리. 기본 `services`(장소 검색).
 *        클러스터링은 화면 좌표 기준으로 직접 묶으므로 `clusterer` 는 쓰지 않는다.
 */
export function loadKakaoMapSdk(libraries = ['services']) {
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    if (window.kakao && window.kakao.maps && window.kakao.maps.LatLng) {
      resolve(window.kakao);
      return;
    }

    const appKey = process.env.VUE_APP_KAKAO_MAP_JAVASCRIPT_KEY;
    if (!appKey) {
      reject(
        new KakaoMapLoadError(
          KAKAO_MAP_ERROR.NO_KEY,
          '카카오맵 JavaScript 키가 설정되지 않았습니다 (VUE_APP_KAKAO_MAP_JAVASCRIPT_KEY).'
        )
      );
      return;
    }

    const existing = document.getElementById(SDK_ID);
    const script = existing || document.createElement('script');

    const done = () => {
      // autoload=false 로 받고 명시적으로 load — 콜백 시점이 보장된다.
      try {
        window.kakao.maps.load(() => resolve(window.kakao));
      } catch (e) {
        reject(new KakaoMapLoadError(KAKAO_MAP_ERROR.LOAD_FAILED, e.message));
      }
    };

    script.addEventListener('load', done);
    script.addEventListener('error', () =>
      reject(
        new KakaoMapLoadError(
          KAKAO_MAP_ERROR.LOAD_FAILED,
          '카카오맵 SDK 를 불러오지 못했습니다. 도메인 미등록(401) 또는 네트워크 문제입니다.'
        )
      )
    );

    if (!existing) {
      script.id = SDK_ID;
      script.async = true;
      const libParam = libraries.length ? `&libraries=${libraries.join(',')}` : '';
      script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${appKey}&autoload=false${libParam}`;
      document.head.appendChild(script);
    }
  });

  // 실패는 캐시하지 않는다 — 네트워크 일시 장애면 재진입 시 다시 시도할 수 있어야 한다.
  loadPromise.catch(() => {
    loadPromise = null;
  });

  return loadPromise;
}
